import { z } from "zod";
//#region src/wallet-services/wallet-api-logs.ts
/**
* Prefix used for synthetic row IDs when wallet_prepareCalls logs do not have
* a real call ID. This can happen for auth-only prepare flows that do not
* build a user operation.
*/
const SYNTHETIC_PREPARE_CALL_ID_PREFIX = "prepare-";
/**
* Wallet API methods that generate logs.
*/
const WalletApiMethodSchema = z.enum([
	"wallet_prepareCalls",
	"wallet_sendPreparedCalls",
	"wallet_getCallsStatus"
]);
/**
* Known status codes from wallet_getCallsStatus response.
* @see StatusCode in wallet-server/types/src/schemas.ts
*/
const WalletStatusCode = {
	PENDING: 100,
	PRECONFIRMED: 110,
	PRECONFIRMED_ONCHAIN_FAILURE: 115,
	PRECONFIRMED_PARTIAL_FAILURE: 116,
	CROSS_CHAIN_PENDING: 120,
	CONFIRMED: 200,
	OFFCHAIN_FAILURE: 400,
	CROSS_CHAIN_REFUNDED: 410,
	ONCHAIN_FAILURE: 500,
	PARTIAL_FAILURE: 600
};
/**
* Status of a call ID group (aggregated status across all events).
*
* Early lifecycle statuses:
* - `prepare_failed`: Error during wallet_prepareCalls
* - `send_failed`: Error during wallet_sendPreparedCalls
* - `status_failed`: Error during wallet_getCallsStatus
* - `prepared`: All calls succeeded, last call was wallet_prepareCalls
* - `sent`: All calls succeeded, last call was wallet_sendPreparedCalls
* - `unknown`: Method could not be determined
*
* Status from wallet_getCallsStatus (mapped from WalletStatusCode):
* - `pending`: (100) Batch received but not yet executed onchain
* - `preconfirmed`: (110) Batch has been preconfirmed onchain
* - `cross_chain_pending`: (120) Origin confirmed, destination pending
* - `confirmed`: (200) Batch included onchain without reverts
* - `offchain_failure`: (400) Batch not included onchain, wallet won't retry
* - `cross_chain_refunded`: (410) Cross-chain operation failed and was refunded
* - `onchain_failure`: (500, 115) Batch reverted completely
* - `partial_failure`: (600, 116) Batch reverted partially
*/
const CallIdGroupStatusSchema = z.enum([
	"prepare_failed",
	"send_failed",
	"status_failed",
	"prepared",
	"sent",
	"unknown",
	"pending",
	"preconfirmed",
	"cross_chain_pending",
	"confirmed",
	"offchain_failure",
	"cross_chain_refunded",
	"onchain_failure",
	"partial_failure"
]);
/**
* Error classification type - the category of the error.
*
* - `execution`: response-body failures from `wallet_getCallsStatus` where
*   the API call succeeded but the result status indicates an on-chain or
*   off-chain execution failure.
*/
const AIErrorTypeSchema = z.enum([
	"validation",
	"sponsorship",
	"session_key",
	"signing",
	"contract",
	"execution"
]);
/**
* Classified error from AI analysis.
*/
const AIErrorExplanationSchema = z.object({
	/** Original error message from the Wallet API response */
	message: z.string(),
	/** Type/category of the error */
	type: AIErrorTypeSchema,
	/** AI-generated cause explanation */
	explanation: z.string(),
	/** AI-generated remediation steps */
	remediation: z.string()
});
const TenderlySimulationResultSchema = z.object({ 
/** Public URL to the shared Tenderly simulation. */
url: z.string() });
/**
* Networks supported by both Alchemy and Tenderly's simulation API.
* Used by the frontend to gate the debug button and by the backend
* to validate before calling the Tenderly API.
*/
const TENDERLY_SUPPORTED_NETWORKS = new Set([
	"ETH_MAINNET",
	"ETH_SEPOLIA",
	"MATIC_MAINNET",
	"MATIC_AMOY",
	"ARB_MAINNET",
	"ARB_SEPOLIA",
	"OPT_MAINNET",
	"OPT_SEPOLIA",
	"BASE_MAINNET",
	"BASE_SEPOLIA",
	"AVAX_MAINNET",
	"AVAX_FUJI",
	"BNB_MAINNET",
	"BNB_TESTNET",
	"GNOSIS_MAINNET",
	"LINEA_MAINNET",
	"LINEA_SEPOLIA",
	"SCROLL_MAINNET",
	"SCROLL_SEPOLIA",
	"BLAST_MAINNET",
	"BLAST_SEPOLIA",
	"ZKSYNC_MAINNET",
	"ZKSYNC_SEPOLIA",
	"FANTOM_MAINNET",
	"POLYGONZKEVM_MAINNET"
]);
/**
* Returns whether the given network is supported by Tenderly's simulation API.
*/
function isTenderlySupportedNetwork(network) {
	if (!network) return false;
	return TENDERLY_SUPPORTED_NETWORKS.has(network);
}
/**
* Event-level error details from the JSON-RPC response.
*/
const WalletCallEventErrorSchema = z.object({
	code: z.number().optional(),
	message: z.string().optional(),
	data: z.unknown().optional(),
	name: z.string().optional()
});
/**
* Subset of `CallIdGroupStatus` that represents actual failure outcomes.
*/
const ResponseFailureTypeSchema = z.enum([
	"offchain_failure",
	"cross_chain_refunded",
	"onchain_failure",
	"partial_failure"
]);
/**
* Derived failure state from a `wallet_getCallsStatus` response where the
* API call succeeded (HTTP 200) but the result status indicates a failure.
*/
const ResponseFailureSchema = z.object({
	/** Group status derived from the status code (e.g., "onchain_failure"). */
	failureType: ResponseFailureTypeSchema,
	/** Raw status code from the response (e.g., 500). */
	statusCode: z.number(),
	/** Human-readable description of the failure for display in the UI. */
	message: z.string(),
	/** Transaction hash from `receipts[0].transactionHash`, when available. */
	transactionHash: z.string().optional()
});
/**
* A single wallet API call event.
*/
const WalletCallEventSchema = z.object({
	callId: z.string().optional(),
	method: WalletApiMethodSchema.optional(),
	status: z.enum(["error", "success"]),
	timestamp: z.string(),
	network: z.string().optional(),
	appId: z.string().optional(),
	appName: z.string().optional(),
	durationMs: z.number().optional(),
	request: z.unknown(),
	response: z.unknown(),
	error: WalletCallEventErrorSchema.optional(),
	responseFailure: ResponseFailureSchema.optional()
});
/**
* Summary of a call ID group (used in list view).
*/
const CallIdGroupSummarySchema = z.object({
	callId: z.string(),
	appId: z.string().optional(),
	appName: z.string().optional(),
	network: z.string().optional(),
	status: CallIdGroupStatusSchema,
	/** ISO timestamp of the most recent event in the group (last activity). */
	timestamp: z.string(),
	/** ISO timestamp of the earliest event in the group (lifecycle start). */
	startedAt: z.string().optional()
});
/**
* Detailed view of a call ID group
*/
const CallIdGroupDetailSchema = CallIdGroupSummarySchema.extend({ events: z.array(WalletCallEventSchema) });
//#endregion
export { AIErrorExplanationSchema, AIErrorTypeSchema, CallIdGroupDetailSchema, CallIdGroupStatusSchema, CallIdGroupSummarySchema, ResponseFailureSchema, ResponseFailureTypeSchema, SYNTHETIC_PREPARE_CALL_ID_PREFIX, TENDERLY_SUPPORTED_NETWORKS, TenderlySimulationResultSchema, WalletApiMethodSchema, WalletCallEventErrorSchema, WalletCallEventSchema, WalletStatusCode, isTenderlySupportedNetwork };

//# sourceMappingURL=wallet-api-logs.js.map